import { Badge } from '@thefarmersfront/kitchen';

import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community';

import { TextButtonCell } from '~/components/common';

import { maskName } from '~/utils/common';
import { formatDate } from '~/utils/date';

import { DATE_FORMAT, DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR } from '~/constants';

import type { TypeDeliveryAgencyOrder } from '~/types/delivery-agency.types';

const getDeliveryAgencyOrdersColumnDefs = ({
  currentPage,
  pageSize,
  onClickViewDetail,
}: {
  currentPage: number;
  pageSize: number;
  onClickViewDetail: (order: TypeDeliveryAgencyOrder) => void;
}) => {
  const columnDefs: ColDef<TypeDeliveryAgencyOrder>[] = [
    {
      headerName: 'No',
      valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1 + currentPage * pageSize,
      width: 96,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'orderStatus',
      headerName: '주문 상태',
      cellDataType: false,
      width: 100,
      cellRenderer: Badge,
      cellRendererParams: ({
        value,
      }: ICellRendererParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['orderStatus']>) => ({
        variant: 'light',
        status: true,
        color: value ? DELIVERY_AGENCY_ORDER_STATUS_BADGE_COLOR[value.code] : undefined,
        children: value?.description || '-',
      }),
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'statusUpdatedAt',
      headerName: '상태 마지막 업데이트 일시',
      width: 170,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['statusUpdatedAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
    {
      field: 'clientOrderCode',
      headerName: '판매처 주문번호',
      width: 160,
    },
    {
      headerName: '',
      minWidth: 80,
      width: 80,
      cellRenderer: TextButtonCell,
      cellRendererParams: {
        children: '상세보기',
        showDashIfNoValue: false,
        onClick: ({ data }: IRowNode<TypeDeliveryAgencyOrder>) => {
          if (!data) return;
          onClickViewDetail(data);
        },
      },
      cellStyle: { display: 'flex', alignItems: 'center' },
    },
    {
      field: 'outboundCode',
      headerName: '출고번호',
      width: 160,
    },
    {
      field: 'estimatedDeliveryDate',
      headerName: '배송 예정일',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['estimatedDeliveryDate']>) =>
        formatDate(value, DATE_FORMAT.DATE) ?? '-',
    },
    {
      field: 'deliveryType.description',
      headerName: '배송 유형',
      width: 80,
    },
    {
      field: 'courierCode.description',
      headerName: '배송사',
      width: 88,
    },
    {
      field: 'receiverName',
      headerName: '수령자명',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['receiverName']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'ordererName',
      headerName: '주문자명',
      width: 100,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['ordererName']>) =>
        maskName(value) ?? '-',
    },
    {
      field: 'productName',
      headerName: '상품명',
      flex: 1,
      minWidth: 252,
      width: 252,
    },
    {
      field: 'createdAt',
      headerName: '주문등록일시',
      width: 147,
      valueFormatter: ({
        value,
      }: ValueFormatterParams<TypeDeliveryAgencyOrder, TypeDeliveryAgencyOrder['createdAt']>) =>
        formatDate(value, DATE_FORMAT.DATE_TIME) ?? '-',
    },
  ];

  return { columnDefs };
};

export default getDeliveryAgencyOrdersColumnDefs;
